import React from 'react';
import { useTranslation } from 'react-i18next';
import LocalizedLink from '../../i18n/LocalizedLink';
import { BlogLayout } from '../../templates/BlogLayout';
import { SEOMeta } from '../../templates/SEOMeta';
import ArticleContainer from '../../components/layout/ArticleContainer';

const BlogModalidadesEs: React.FC = () => {
  return (
    <BlogLayout maxWidth="normal">
      <SEOMeta
        title="Modalidades de Juego - El Impostor"
        description="Descubre las diferentes modalidades de juego de El Impostor: multijugador online en tiempo real y modo mesa presencial."
        canonical="https://el-impostor.com/blog/modalidad"
        keywords="impostor modalidades, impostor online, impostor mesa, modos de juego impostor"
      />
      {/* Breadcrumb */}
      <div className="mb-6 text-sm text-gray-600 dark:text-gray-300">
        <LocalizedLink to="/" className="text-indigo-600 dark:text-indigo-300 hover:underline">← Inicio</LocalizedLink>
        {' '}/{' '}
        <LocalizedLink to="/blog" className="text-indigo-600 dark:text-indigo-300 hover:underline">Blog</LocalizedLink>
        {' '}/ Modalidades
      </div>

      {/* Header */}
      <div className="text-center mb-10">
        <h1 className="text-5xl font-extrabold text-indigo-800 dark:text-indigo-200 mb-3">
          🎮 Modalidades de Juego
        </h1>
        <p className="text-xl text-gray-600 dark:text-gray-300">
          Elige tu forma favorita de jugar
        </p>
      </div>

      <ArticleContainer>
        <p className="text-lg text-gray-700 dark:text-gray-200 mb-8">
          <strong>Impostor Game</strong> ofrece diferentes modalidades para adaptarse a tus necesidades.
        </p>

        {/* Modalidad Multijugador */}
        <div className="bg-gradient-to-br from-blue-50 to-white dark:from-gray-900 dark:to-gray-950 border-2 border-blue-300 dark:border-gray-800 rounded-xl p-8 mb-8 shadow-lg">
          <div className="flex items-center gap-3 mb-4">
            <h2 className="text-3xl font-bold text-indigo-800">
              🌐 Modalidad Multijugador
            </h2>
            <span className="px-3 py-1 bg-green-500 text-white text-xs font-bold rounded-full uppercase">
              Disponible
            </span>
          </div>
          
          <p className="text-gray-700 dark:text-gray-200 mb-6">
            Juega con amigos desde <strong>cualquier dispositivo</strong> en <strong>tiempo real</strong>.
          </p>

          <h3 className="text-xl font-semibold text-indigo-700 mb-3">
            ✨ Características
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div className="flex gap-3 p-3 bg-white dark:bg-gray-900 rounded-lg border border-blue-100 dark:border-gray-800">
              <div className="text-2xl">💻📱</div>
              <div>
                <strong className="block text-gray-900 dark:text-gray-100">Crossplatform</strong>
                <span className="text-sm text-gray-600 dark:text-gray-300">PC, móvil y tablet en la misma partida</span>
              </div>
            </div>
            <div className="flex gap-3 p-3 bg-white dark:bg-gray-900 rounded-lg border border-blue-100 dark:border-gray-800">
              <div className="text-2xl">⚡</div>
              <div>
                <strong className="block text-gray-900 dark:text-gray-100">Tiempo Real</strong>
                <span className="text-sm text-gray-600 dark:text-gray-300">WebSockets instantáneos</span>
              </div>
            </div>
            <div className="flex gap-3 p-3 bg-white dark:bg-gray-900 rounded-lg border border-blue-100 dark:border-gray-800">
              <div className="text-2xl">🌐</div>
              <div>
                <strong className="block text-gray-900 dark:text-gray-100">Sin Descargas</strong>
                <span className="text-sm text-gray-600 dark:text-gray-300">100% navegador web</span>
              </div>
            </div>
            <div className="flex gap-3 p-3 bg-white dark:bg-gray-900 rounded-lg border border-blue-100 dark:border-gray-800">
              <div className="text-2xl">🔗</div>
              <div>
                <strong className="block text-gray-900 dark:text-gray-100">Conexión Simple</strong>
                <span className="text-sm text-gray-600 dark:text-gray-300">Código de 6 caracteres</span>
              </div>
            </div>
          </div>

          <h3 className="text-xl font-semibold text-indigo-700 mb-3">
            📋 Requerimientos
          </h3>
          <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-200 mb-6">
            <li>Conexión a Internet estable</li>
            <li>Navegador moderno (Chrome, Firefox, Safari, Edge)</li>
            <li>Cada jugador necesita su dispositivo</li>
            <li>3-10 jugadores por sala</li>
          </ul>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            {/* Ventajas */}
            <div className="p-4 bg-green-50 dark:bg-green-900/20 border-2 border-green-200 dark:border-green-800/30 rounded-lg">
              <h4 className="text-lg font-semibold text-green-700 mb-3">✅ Ventajas</h4>
              <ul className="list-disc list-inside space-y-1 text-sm text-gray-700 dark:text-gray-200">
                <li>Juega desde cualquier lugar</li>
                <li>No requiere estar físicamente juntos</li>
                <li>Pantalla privada por jugador</li>
                <li>Comunicación instantánea</li>
                <li>Reconexión automática</li>
              </ul>
            </div>

            {/* Consideraciones */}
            <div className="p-4 bg-orange-50 dark:bg-orange-900/20 border-2 border-orange-200 dark:border-orange-800/30 rounded-lg">
              <h4 className="text-lg font-semibold text-orange-700 mb-3">⚠️ Consideraciones</h4>
              <ul className="list-disc list-inside space-y-1 text-sm text-gray-700 dark:text-gray-200">
                <li>Requiere Internet estable</li>
                <li>Cada jugador necesita dispositivo</li>
                <li>Consumo de datos móviles mínimo</li>
              </ul>
            </div>
          </div>

          <div className="text-center">
            <LocalizedLink 
              to="/blog/modalidad/multijugador" 
              className="inline-block px-6 py-3 bg-white dark:bg-gray-900 border-2 border-indigo-600 dark:border-indigo-500 text-indigo-600 dark:text-indigo-200 rounded-lg font-semibold hover:bg-indigo-50 dark:hover:bg-gray-800 transition-colors mr-3"
            >
              📖 Leer más
            </LocalizedLink>
            <LocalizedLink 
              to="/create" 
              className="inline-block px-6 py-3 bg-indigo-600 text-white rounded-lg font-semibold hover:bg-indigo-700 transition-colors"
            >
              🚀 Crear Sala
            </LocalizedLink>
          </div>
        </div>

        {/* Modo Mesa */}
        <div className="bg-gradient-to-br from-purple-50 to-white dark:from-gray-900 dark:to-gray-950 border-2 border-purple-400 dark:border-gray-800 rounded-xl p-8 shadow-lg">
          <div className="flex items-center gap-3 mb-4">
            <h2 className="text-3xl font-bold bg-gradient-to-r from-purple-700 to-pink-700 bg-clip-text text-transparent">
              🎲 Modo Mesa
            </h2>
            <span className="px-3 py-1 bg-green-500 text-white text-xs font-bold rounded-full uppercase">
              Disponible
            </span>
          </div>
          
          <p className="text-gray-700 dark:text-gray-200 mb-6">
            Comparte una sola pantalla en <strong>reuniones presenciales</strong> o eventos en vivo.
          </p>

          <h3 className="text-xl font-semibold text-purple-700 mb-3">
            ✨ Características
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div className="flex gap-3 p-3 bg-white dark:bg-gray-900 rounded-lg border border-purple-100 dark:border-gray-800">
              <div className="text-2xl">📺</div>
              <div>
                <strong className="block text-gray-900 dark:text-gray-100">Una Sola Pantalla</strong>
                <span className="text-sm text-gray-600 dark:text-gray-300">Todos ven la misma interfaz</span>
              </div>
            </div>
            <div className="flex gap-3 p-3 bg-white dark:bg-gray-900 rounded-lg border border-purple-100 dark:border-gray-800">
              <div className="text-2xl">👥</div>
              <div>
                <strong className="block text-gray-900 dark:text-gray-100">Presencial</strong>
                <span className="text-sm text-gray-600 dark:text-gray-300">Ideal para reuniones cara a cara</span>
              </div>
            </div>
            <div className="flex gap-3 p-3 bg-white dark:bg-gray-900 rounded-lg border border-purple-100 dark:border-gray-800">
              <div className="text-2xl">🎭</div>
              <div>
                <strong className="block text-gray-900 dark:text-gray-100">Roles Privados</strong>
                <span className="text-sm text-gray-600 dark:text-gray-300">Cada jugador ve su rol individualmente</span>
              </div>
            </div>
            <div className="flex gap-3 p-3 bg-white dark:bg-gray-900 rounded-lg border border-purple-100 dark:border-gray-800">
              <div className="text-2xl">🎉</div>
              <div>
                <strong className="block text-gray-900 dark:text-gray-100">Eventos y Fiestas</strong>
                <span className="text-sm text-gray-600 dark:text-gray-300">Perfecto para grupos presenciales</span>
              </div>
            </div>
          </div>

          <h3 className="text-xl font-semibold text-purple-700 mb-3">
            📋 Requerimientos
          </h3>
          <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-200 mb-6">
            <li>Un solo dispositivo (tablet, laptop o pantalla grande recomendada)</li>
            <li>Todos los jugadores en el mismo lugar</li>
            <li>3-10 jugadores</li>
            <li>Conexión a Internet solo para el host</li>
          </ul>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            {/* Ventajas */}
            <div className="p-4 bg-green-50 dark:bg-green-900/20 border-2 border-green-200 dark:border-green-800/30 rounded-lg">
              <h4 className="text-lg font-semibold text-green-700 mb-3">✅ Ventajas</h4>
              <ul className="list-disc list-inside space-y-1 text-sm text-gray-700 dark:text-gray-200">
                <li>Solo necesitas un dispositivo</li>
                <li>Interacción cara a cara</li>
                <li>Observa lenguaje corporal</li>
                <li>Perfecto para fiestas y aulas</li>
                <li>No requiere dispositivos individuales</li>
              </ul>
            </div>

            {/* Consideraciones */}
            <div className="p-4 bg-orange-50 dark:bg-orange-900/20 border-2 border-orange-200 dark:border-orange-800/30 rounded-lg">
              <h4 className="text-lg font-semibold text-orange-700 mb-3">⚠️ Consideraciones</h4>
              <ul className="list-disc list-inside space-y-1 text-sm text-gray-700 dark:text-gray-200">
                <li>Requiere estar en el mismo lugar</li>
                <li>Pantalla compartida (menos privacidad)</li>
                <li>Mejor con pantalla grande</li>
              </ul>
            </div>
          </div>

          <div className="text-center">
            <LocalizedLink 
              to="/blog/modalidad/mesa" 
              className="inline-block px-6 py-3 bg-white dark:bg-gray-900 border-2 border-purple-600 dark:border-purple-400 text-purple-600 dark:text-purple-200 rounded-lg font-semibold hover:bg-purple-50 dark:hover:bg-gray-800 transition-colors mr-3"
            >
              📖 Leer más
            </LocalizedLink>
            <LocalizedLink 
              to="/table" 
              className="inline-block px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg font-semibold hover:from-purple-700 hover:to-pink-700 transition-colors"
            >
              🎲 Jugar en Mesa
            </LocalizedLink>
          </div>
        </div>

        {/* Comparación */}
        <div className="mt-12">
          <h2 className="text-2xl font-bold text-indigo-800 mb-6 text-center">
            📊 Comparación de Modalidades
          </h2>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-gray-900 dark:text-gray-100">
              <thead>
                <tr className="bg-indigo-100 dark:bg-gray-900">
                  <th className="border border-indigo-300 dark:border-gray-800 px-4 py-3 text-left">Característica</th>
                  <th className="border border-indigo-300 dark:border-gray-800 px-4 py-3 text-center">Multijugador</th>
                  <th className="border border-indigo-300 dark:border-gray-800 px-4 py-3 text-center">Modo Mesa</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="border border-gray-300 dark:border-gray-800 px-4 py-2">Dispositivos necesarios</td>
                  <td className="border border-gray-300 dark:border-gray-800 px-4 py-2 text-center">Uno por jugador</td>
                  <td className="border border-gray-300 dark:border-gray-800 px-4 py-2 text-center">Solo uno</td>
                </tr>
                <tr className="bg-gray-50 dark:bg-gray-800/50">
                  <td className="border border-gray-300 dark:border-gray-800 px-4 py-2">Conexión a Internet</td>
                  <td className="border border-gray-300 dark:border-gray-800 px-4 py-2 text-center">Todos</td>
                  <td className="border border-gray-300 dark:border-gray-800 px-4 py-2 text-center">Solo host</td>
                </tr>
                <tr>
                  <td className="border border-gray-300 dark:border-gray-800 px-4 py-2">Juego remoto</td>
                  <td className="border border-gray-300 dark:border-gray-800 px-4 py-2 text-center">✅ Sí</td>
                  <td className="border border-gray-300 dark:border-gray-800 px-4 py-2 text-center">❌ No</td>
                </tr>
                <tr className="bg-gray-50 dark:bg-gray-800/50">
                  <td className="border border-gray-300 dark:border-gray-800 px-4 py-2">Juego presencial</td>
                  <td className="border border-gray-300 dark:border-gray-800 px-4 py-2 text-center">✅ Sí</td>
                  <td className="border border-gray-300 dark:border-gray-800 px-4 py-2 text-center">✅ Sí</td>
                </tr>
                <tr>
                  <td className="border border-gray-300 dark:border-gray-800 px-4 py-2">Privacidad de roles</td>
                  <td className="border border-gray-300 dark:border-gray-800 px-4 py-2 text-center">Alta (pantalla individual)</td>
                  <td className="border border-gray-300 dark:border-gray-800 px-4 py-2 text-center">Alta (vista individual por turno)</td>
                </tr>
                <tr className="bg-gray-50 dark:bg-gray-800/50">
                  <td className="border border-gray-300 dark:border-gray-800 px-4 py-2">Ideal para</td>
                  <td className="border border-gray-300 dark:border-gray-800 px-4 py-2 text-center">Juego remoto</td>
                  <td className="border border-gray-300 dark:border-gray-800 px-4 py-2 text-center">Fiestas y reuniones</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* CTA Final */}
        <div className="mt-12 text-center bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-gray-900 dark:to-gray-950 border-2 border-indigo-200 dark:border-gray-800 rounded-xl p-8">
          <h3 className="text-2xl font-bold text-indigo-900 dark:text-gray-100 mb-3">
            ¿Listo para jugar?
          </h3>
          <p className="text-gray-700 dark:text-gray-200 mb-6">
            Elige tu modalidad favorita y empieza a jugar
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <LocalizedLink 
              to="/create" 
              className="px-8 py-4 bg-indigo-600 text-white rounded-lg font-bold text-lg hover:bg-indigo-700 transition-colors shadow-lg"
            >
              🌐 Jugar Online
            </LocalizedLink>
            <LocalizedLink 
              to="/table" 
              className="px-8 py-4 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg font-bold text-lg hover:from-purple-700 hover:to-pink-700 transition-colors shadow-lg"
            >
              🎲 Jugar en Mesa
            </LocalizedLink>
          </div>
        </div>
      </ArticleContainer>
    </BlogLayout>
  );
};

const BlogModalidadesPt: React.FC = () => {
  return (
    <BlogLayout maxWidth="normal">
      <SEOMeta
        title="Modalidades de Jogo - El Impostor"
        description="Descubra as diferentes modalidades de jogo do El Impostor: multijogador online em tempo real e modo mesa presencial."
        canonical="https://el-impostor.com/blog/modalidad"
        keywords="impostor modalidades, impostor online, impostor mesa, modos de jogo impostor"
      />
      {/* Breadcrumb */}
      <div className="mb-6 text-sm text-gray-600 dark:text-gray-300">
        <LocalizedLink to="/" className="text-indigo-600 dark:text-indigo-300 hover:underline">← Início</LocalizedLink>
        {' '}/{' '}
        <LocalizedLink to="/blog" className="text-indigo-600 dark:text-indigo-300 hover:underline">Blog</LocalizedLink>
        {' '}/ Modalidades
      </div>

      {/* Header */}
      <div className="text-center mb-10">
        <h1 className="text-5xl font-extrabold text-indigo-800 dark:text-indigo-200 mb-3">
          🎮 Modalidades de Jogo
        </h1>
        <p className="text-xl text-gray-600 dark:text-gray-300">
          Escolha sua forma favorita de jogar
        </p>
      </div>

      <ArticleContainer>
        <p className="text-lg text-gray-700 dark:text-gray-200 mb-8">
          <strong>Impostor Game</strong> oferece diferentes modalidades para se adaptar às suas necessidades.
        </p>

        {/* Modalidade Multijogador */}
        <div className="bg-gradient-to-br from-blue-50 to-white dark:from-gray-900 dark:to-gray-950 border-2 border-blue-300 dark:border-gray-800 rounded-xl p-8 mb-8 shadow-lg">
          <div className="flex items-center gap-3 mb-4">
            <h2 className="text-3xl font-bold text-indigo-800">
              🌐 Modalidade Multijogador
            </h2>
            <span className="px-3 py-1 bg-green-500 text-white text-xs font-bold rounded-full uppercase">
              Disponível
            </span>
          </div>
          
          <p className="text-gray-700 dark:text-gray-200 mb-6">
            Jogue com amigos de <strong>qualquer dispositivo</strong> em <strong>tempo real</strong>.
          </p>

          <h3 className="text-xl font-semibold text-indigo-700 mb-3">
            ✨ Características
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div className="flex gap-3 p-3 bg-white dark:bg-gray-900 rounded-lg border border-blue-100 dark:border-gray-800">
              <div className="text-2xl">💻📱</div>
              <div>
                <strong className="block text-gray-900 dark:text-gray-100">Crossplatform</strong>
                <span className="text-sm text-gray-600 dark:text-gray-300">PC, celular e tablet na mesma partida</span>
              </div>
            </div>
            <div className="flex gap-3 p-3 bg-white dark:bg-gray-900 rounded-lg border border-blue-100 dark:border-gray-800">
              <div className="text-2xl">⚡</div>
              <div>
                <strong className="block text-gray-900 dark:text-gray-100">Tempo Real</strong>
                <span className="text-sm text-gray-600 dark:text-gray-300">WebSockets instantâneos</span>
              </div>
            </div>
            <div className="flex gap-3 p-3 bg-white dark:bg-gray-900 rounded-lg border border-blue-100 dark:border-gray-800">
              <div className="text-2xl">🌐</div>
              <div>
                <strong className="block text-gray-900 dark:text-gray-100">Sem Downloads</strong>
                <span className="text-sm text-gray-600 dark:text-gray-300">100% navegador web</span>
              </div>
            </div>
            <div className="flex gap-3 p-3 bg-white dark:bg-gray-900 rounded-lg border border-blue-100 dark:border-gray-800">
              <div className="text-2xl">🔗</div>
              <div>
                <strong className="block text-gray-900 dark:text-gray-100">Conexão Simples</strong>
                <span className="text-sm text-gray-600 dark:text-gray-300">Código de 6 caracteres</span>
              </div>
            </div>
          </div>

          <h3 className="text-xl font-semibold text-indigo-700 mb-3">
            📋 Requisitos
          </h3>
          <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-200 mb-6">
            <li>Conexão de internet estável</li>
            <li>Navegador moderno (Chrome, Firefox, Safari, Edge)</li>
            <li>Cada jogador precisa do seu dispositivo</li>
            <li>3-10 jogadores por sala</li>
          </ul>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            {/* Vantagens */}
            <div className="p-4 bg-green-50 dark:bg-green-900/20 border-2 border-green-200 dark:border-green-800/30 rounded-lg">
              <h4 className="text-lg font-semibold text-green-700 mb-3">✅ Vantagens</h4>
              <ul className="list-disc list-inside space-y-1 text-sm text-gray-700 dark:text-gray-200">
                <li>Jogue de qualquer lugar</li>
                <li>Não é necessário estar fisicamente juntos</li>
                <li>Tela privada por jogador</li>
                <li>Comunicação instantânea</li>
                <li>Reconexão automática</li>
              </ul>
            </div>

            {/* Considerações */}
            <div className="p-4 bg-orange-50 dark:bg-orange-900/20 border-2 border-orange-200 dark:border-orange-800/30 rounded-lg">
              <h4 className="text-lg font-semibold text-orange-700 mb-3">⚠️ Considerações</h4>
              <ul className="list-disc list-inside space-y-1 text-sm text-gray-700 dark:text-gray-200">
                <li>Requer internet estável</li>
                <li>Cada jogador precisa de dispositivo</li>
                <li>Consumo mínimo de dados móveis</li>
              </ul>
            </div>
          </div>

          <div className="flex justify-center">
            <LocalizedLink to="/blog/modalidad/multijugador" className="btn-primary px-6 py-3">
              Saiba mais sobre o Modo Multijogador
            </LocalizedLink>
          </div>
        </div>

        {/* Modalidade Mesa */}
        <div className="bg-gradient-to-br from-purple-50 to-white dark:from-gray-900 dark:to-gray-950 border-2 border-purple-300 dark:border-gray-800 rounded-xl p-8 mb-8 shadow-lg">
          <div className="flex items-center gap-3 mb-4">
            <h2 className="text-3xl font-bold text-purple-800">
              🎲 Modalidade Mesa
            </h2>
            <span className="px-3 py-1 bg-purple-500 text-white text-xs font-bold rounded-full uppercase">
              Disponível
            </span>
          </div>

          <p className="text-gray-700 dark:text-gray-200 mb-6">
            Perfeito para <strong>reuniões presenciais</strong> com um único dispositivo.
          </p>

          <h3 className="text-xl font-semibold text-purple-700 mb-3">
            ✨ Características
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div className="flex gap-3 p-3 bg-white dark:bg-gray-900 rounded-lg border border-purple-100 dark:border-gray-800">
              <div className="text-2xl">📱</div>
              <div>
                <strong className="block text-gray-900 dark:text-gray-100">Um dispositivo</strong>
                <span className="text-sm text-gray-600 dark:text-gray-300">Todos jogam no mesmo aparelho</span>
              </div>
            </div>
            <div className="flex gap-3 p-3 bg-white dark:bg-gray-900 rounded-lg border border-purple-100 dark:border-gray-800">
              <div className="text-2xl">🃏</div>
              <div>
                <strong className="block text-gray-900 dark:text-gray-100">Cartas virtuais</strong>
                <span className="text-sm text-gray-600 dark:text-gray-300">Revelação individual de papéis</span>
              </div>
            </div>
            <div className="flex gap-3 p-3 bg-white dark:bg-gray-900 rounded-lg border border-purple-100 dark:border-gray-800">
              <div className="text-2xl">🗳️</div>
              <div>
                <strong className="block text-gray-900 dark:text-gray-100">Votação presencial</strong>
                <span className="text-sm text-gray-600 dark:text-gray-300">Vote levantando a mão ou com o dispositivo</span>
              </div>
            </div>
            <div className="flex gap-3 p-3 bg-white dark:bg-gray-900 rounded-lg border border-purple-100 dark:border-gray-800">
              <div className="text-2xl">🛋️</div>
              <div>
                <strong className="block text-gray-900 dark:text-gray-100">Interação cara a cara</strong>
                <span className="text-sm text-gray-600 dark:text-gray-300">Experiência social mais intensa</span>
              </div>
            </div>
          </div>

          <h3 className="text-xl font-semibold text-purple-700 mb-3">
            📋 Requisitos
          </h3>
          <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-200 mb-6">
            <li>Um único dispositivo (celular, tablet ou laptop)</li>
            <li>Grupo presencial reunido no mesmo local</li>
            <li>3-10 jogadores</li>
            <li>Internet somente para carregar a página</li>
          </ul>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            {/* Vantagens */}
            <div className="p-4 bg-green-50 dark:bg-green-900/20 border-2 border-green-200 dark:border-green-800/30 rounded-lg">
              <h4 className="text-lg font-semibold text-green-700 mb-3">✅ Vantagens</h4>
              <ul className="list-disc list-inside space-y-1 text-sm text-gray-700 dark:text-gray-200">
                <li>Perfeito para festas e reuniões</li>
                <li>Maior interação social</li>
                <li>Sem necessidade de vários dispositivos</li>
                <li>Discussões cara a cara</li>
                <li>Experiência mais imersiva</li>
              </ul>
            </div>

            {/* Considerações */}
            <div className="p-4 bg-orange-50 dark:bg-orange-900/20 border-2 border-orange-200 dark:border-orange-800/30 rounded-lg">
              <h4 className="text-lg font-semibold text-orange-700 mb-3">⚠️ Considerações</h4>
              <ul className="list-disc list-inside space-y-1 text-sm text-gray-700 dark:text-gray-200">
                <li>Jogadores precisam estar juntos</li>
                <li>Privacidade depende do grupo</li>
                <li>Não é ideal para jogos remotos</li>
              </ul>
            </div>
          </div>

          <div className="flex justify-center">
            <LocalizedLink to="/blog/modalidad/mesa" className="btn-secondary px-6 py-3">
              Saiba mais sobre o Modo Mesa
            </LocalizedLink>
          </div>
        </div>

        {/* Comparação */}
        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-8 shadow-lg">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-6 text-center">
            ⚖️ Comparação: Online vs Mesa
          </h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left text-gray-700 dark:text-gray-200">
              <thead className="bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-gray-100 uppercase text-xs">
                <tr>
                  <th className="px-4 py-2">Aspecto</th>
                  <th className="px-4 py-2 text-center">Online</th>
                  <th className="px-4 py-2 text-center">Mesa</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="border border-gray-300 dark:border-gray-800 px-4 py-2">Dispositivos necessários</td>
                  <td className="border border-gray-300 dark:border-gray-800 px-4 py-2 text-center">1 por jogador</td>
                  <td className="border border-gray-300 dark:border-gray-800 px-4 py-2 text-center">1 dispositivo total</td>
                </tr>
                <tr className="bg-gray-50 dark:bg-gray-800/50">
                  <td className="border border-gray-300 dark:border-gray-800 px-4 py-2">Localização</td>
                  <td className="border border-gray-300 dark:border-gray-800 px-4 py-2 text-center">Qualquer lugar</td>
                  <td className="border border-gray-300 dark:border-gray-800 px-4 py-2 text-center">Mesmo lugar</td>
                </tr>
                <tr>
                  <td className="border border-gray-300 dark:border-gray-800 px-4 py-2">Interação</td>
                  <td className="border border-gray-300 dark:border-gray-800 px-4 py-2 text-center">Virtual/texto</td>
                  <td className="border border-gray-300 dark:border-gray-800 px-4 py-2 text-center">Cara a cara</td>
                </tr>
                <tr className="bg-gray-50 dark:bg-gray-800/50">
                  <td className="border border-gray-300 dark:border-gray-800 px-4 py-2">Privacidade dos papéis</td>
                  <td className="border border-gray-300 dark:border-gray-800 px-4 py-2 text-center">Alta (tela individual)</td>
                  <td className="border border-gray-300 dark:border-gray-800 px-4 py-2 text-center">Alta (turnos individuais)</td>
                </tr>
                <tr>
                  <td className="border border-gray-300 dark:border-gray-800 px-4 py-2">Melhor para</td>
                  <td className="border border-gray-300 dark:border-gray-800 px-4 py-2 text-center">Jogar à distância</td>
                  <td className="border border-gray-300 dark:border-gray-800 px-4 py-2 text-center">Festas e encontros</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* CTA final */}
        <div className="mt-12 text-center bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-gray-900 dark:to-gray-950 border-2 border-indigo-200 dark:border-gray-800 rounded-xl p-8">
          <h3 className="text-2xl font-bold text-indigo-900 dark:text-gray-100 mb-3">Pronto para jogar?</h3>
          <p className="text-gray-700 dark:text-gray-200 mb-6">Escolha sua modalidade favorita e comece a jogar</p>
          <div className="flex flex-wrap justify-center gap-3">
            <LocalizedLink
              to="/create"
              className="px-8 py-4 bg-indigo-600 text-white rounded-lg font-bold text-lg hover:bg-indigo-700 transition-colors shadow-lg"
            >
              🌐 Jogar Online
            </LocalizedLink>
            <LocalizedLink
              to="/table"
              className="px-8 py-4 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg font-bold text-lg hover:from-purple-700 hover:to-pink-700 transition-colors shadow-lg"
            >
              🎲 Jogar Modo Mesa
            </LocalizedLink>
          </div>
        </div>
      </ArticleContainer>
    </BlogLayout>
  );
};

const BlogModalidadesEn: React.FC = () => {
  return (
    <BlogLayout maxWidth="normal">
      <SEOMeta
        title="Game Modes - Impostor Game"
        description="Explore Impostor Game modes: real-time online multiplayer and in-person table mode."
        canonical="https://el-impostor.com/blog/modalidad"
        keywords="impostor game modes, impostor online, impostor table mode, how to play impostor"
      />
      {/* Breadcrumb */}
      <div className="mb-6 text-sm text-gray-600 dark:text-gray-300">
        <LocalizedLink to="/" className="text-indigo-600 dark:text-indigo-300 hover:underline">← Home</LocalizedLink>
        {' '}/{' '}
        <LocalizedLink to="/blog" className="text-indigo-600 dark:text-indigo-300 hover:underline">Blog</LocalizedLink>
        {' '}/ Game modes
      </div>

      {/* Header */}
      <div className="text-center mb-10">
        <h1 className="text-5xl font-extrabold text-indigo-800 dark:text-indigo-200 mb-3">🎮 Game Modes</h1>
        <p className="text-xl text-gray-600 dark:text-gray-300">Pick your favorite way to play</p>
      </div>

      <ArticleContainer>
        <p className="text-lg text-gray-700 dark:text-gray-200 mb-8">
          <strong>Impostor Game</strong> offers different modes to fit your needs.
        </p>

        {/* Multiplayer mode */}
        <div className="bg-gradient-to-br from-blue-50 to-white dark:from-gray-900 dark:to-gray-950 border-2 border-blue-300 dark:border-gray-800 rounded-xl p-8 mb-8 shadow-lg">
          <div className="flex items-center gap-3 mb-4">
            <h2 className="text-3xl font-bold text-indigo-800">🌐 Multiplayer Mode</h2>
            <span className="px-3 py-1 bg-green-500 text-white text-xs font-bold rounded-full uppercase">
              Available
            </span>
          </div>

          <p className="text-gray-700 dark:text-gray-200 mb-6">
            Play with friends from <strong>any device</strong> in <strong>real time</strong>.
          </p>

          <h3 className="text-xl font-semibold text-indigo-700 mb-3">✨ Features</h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div className="flex gap-3 p-3 bg-white dark:bg-gray-900 rounded-lg border border-blue-100 dark:border-gray-800">
              <div className="text-2xl">💻📱</div>
              <div>
                <strong className="block text-gray-900 dark:text-gray-100">Cross-platform</strong>
                <span className="text-sm text-gray-600 dark:text-gray-300">PC, mobile, and tablet in the same match</span>
              </div>
            </div>
            <div className="flex gap-3 p-3 bg-white dark:bg-gray-900 rounded-lg border border-blue-100 dark:border-gray-800">
              <div className="text-2xl">⚡</div>
              <div>
                <strong className="block text-gray-900 dark:text-gray-100">Real Time</strong>
                <span className="text-sm text-gray-600 dark:text-gray-300">Instant WebSockets updates</span>
              </div>
            </div>
            <div className="flex gap-3 p-3 bg-white dark:bg-gray-900 rounded-lg border border-blue-100 dark:border-gray-800">
              <div className="text-2xl">🌐</div>
              <div>
                <strong className="block text-gray-900 dark:text-gray-100">No Downloads</strong>
                <span className="text-sm text-gray-600 dark:text-gray-300">100% in your web browser</span>
              </div>
            </div>
            <div className="flex gap-3 p-3 bg-white dark:bg-gray-900 rounded-lg border border-blue-100 dark:border-gray-800">
              <div className="text-2xl">🔗</div>
              <div>
                <strong className="block text-gray-900 dark:text-gray-100">Simple Join</strong>
                <span className="text-sm text-gray-600 dark:text-gray-300">6-character room code</span>
              </div>
            </div>
          </div>

          <h3 className="text-xl font-semibold text-indigo-700 mb-3">📋 Requirements</h3>
          <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-200 mb-6">
            <li>Stable internet connection</li>
            <li>Modern browser (Chrome, Firefox, Safari, Edge)</li>
            <li>Each player needs a device</li>
            <li>3–10 players per room</li>
          </ul>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div className="p-4 bg-green-50 dark:bg-green-900/20 border-2 border-green-200 dark:border-green-800/30 rounded-lg">
              <h4 className="text-lg font-semibold text-green-700 mb-3">✅ Pros</h4>
              <ul className="list-disc list-inside space-y-1 text-sm text-gray-700 dark:text-gray-200">
                <li>Play from anywhere</li>
                <li>No need to be physically together</li>
                <li>Private screen per player</li>
                <li>Instant communication</li>
                <li>Automatic reconnection</li>
              </ul>
            </div>

            <div className="p-4 bg-orange-50 dark:bg-orange-900/20 border-2 border-orange-200 dark:border-orange-800/30 rounded-lg">
              <h4 className="text-lg font-semibold text-orange-700 mb-3">⚠️ Considerations</h4>
              <ul className="list-disc list-inside space-y-1 text-sm text-gray-700 dark:text-gray-200">
                <li>Requires stable internet</li>
                <li>Each player needs a device</li>
                <li>Minimal mobile data usage</li>
              </ul>
            </div>
          </div>

          <div className="text-center">
            <LocalizedLink
              to="/blog/modalidad/multijugador"
              className="inline-block px-6 py-3 bg-white dark:bg-gray-900 border-2 border-indigo-600 dark:border-indigo-500 text-indigo-600 dark:text-indigo-200 rounded-lg font-semibold hover:bg-indigo-50 dark:hover:bg-gray-800 transition-colors mr-3"
            >
              📖 Read more
            </LocalizedLink>
            <LocalizedLink
              to="/create"
              className="inline-block px-6 py-3 bg-indigo-600 text-white rounded-lg font-semibold hover:bg-indigo-700 transition-colors"
            >
              🚀 Create Room
            </LocalizedLink>
          </div>
        </div>

        {/* Table mode */}
        <div className="bg-gradient-to-br from-purple-50 to-white dark:from-gray-900 dark:to-gray-950 border-2 border-purple-400 dark:border-gray-800 rounded-xl p-8 shadow-lg">
          <div className="flex items-center gap-3 mb-4">
            <h2 className="text-3xl font-bold bg-gradient-to-r from-purple-700 to-pink-700 bg-clip-text text-transparent">
              🎲 Table Mode
            </h2>
            <span className="px-3 py-1 bg-green-500 text-white text-xs font-bold rounded-full uppercase">
              Available
            </span>
          </div>

          <p className="text-gray-700 dark:text-gray-200 mb-6">
            Share a single screen in <strong>in-person gatherings</strong> or live events.
          </p>

          <h3 className="text-xl font-semibold text-purple-700 mb-3">✨ Features</h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div className="flex gap-3 p-3 bg-white dark:bg-gray-900 rounded-lg border border-purple-100 dark:border-gray-800">
              <div className="text-2xl">📺</div>
              <div>
                <strong className="block text-gray-900 dark:text-gray-100">Single Screen</strong>
                <span className="text-sm text-gray-600 dark:text-gray-300">Everyone sees the same interface</span>
              </div>
            </div>
            <div className="flex gap-3 p-3 bg-white dark:bg-gray-900 rounded-lg border border-purple-100 dark:border-gray-800">
              <div className="text-2xl">👥</div>
              <div>
                <strong className="block text-gray-900 dark:text-gray-100">In Person</strong>
                <span className="text-sm text-gray-600 dark:text-gray-300">Perfect for face-to-face meetups</span>
              </div>
            </div>
            <div className="flex gap-3 p-3 bg-white dark:bg-gray-900 rounded-lg border border-purple-100 dark:border-gray-800">
              <div className="text-2xl">🎭</div>
              <div>
                <strong className="block text-gray-900 dark:text-gray-100">Private Roles</strong>
                <span className="text-sm text-gray-600 dark:text-gray-300">Each player views their role individually</span>
              </div>
            </div>
            <div className="flex gap-3 p-3 bg-white dark:bg-gray-900 rounded-lg border border-purple-100 dark:border-gray-800">
              <div className="text-2xl">🎉</div>
              <div>
                <strong className="block text-gray-900 dark:text-gray-100">Events & Parties</strong>
                <span className="text-sm text-gray-600 dark:text-gray-300">Great for local groups</span>
              </div>
            </div>
          </div>

          <h3 className="text-xl font-semibold text-purple-700 mb-3">📋 Requirements</h3>
          <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-200 mb-6">
            <li>One device (tablet, laptop, or large screen recommended)</li>
            <li>All players in the same place</li>
            <li>3–10 players</li>
            <li>Internet only required for the host to load the page</li>
          </ul>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div className="p-4 bg-green-50 dark:bg-green-900/20 border-2 border-green-200 dark:border-green-800/30 rounded-lg">
              <h4 className="text-lg font-semibold text-green-700 mb-3">✅ Pros</h4>
              <ul className="list-disc list-inside space-y-1 text-sm text-gray-700 dark:text-gray-200">
                <li>Only one device needed</li>
                <li>Face-to-face interaction</li>
                <li>Read body language</li>
                <li>Great for parties and classrooms</li>
                <li>No individual devices required</li>
              </ul>
            </div>

            <div className="p-4 bg-orange-50 dark:bg-orange-900/20 border-2 border-orange-200 dark:border-orange-800/30 rounded-lg">
              <h4 className="text-lg font-semibold text-orange-700 mb-3">⚠️ Considerations</h4>
              <ul className="list-disc list-inside space-y-1 text-sm text-gray-700 dark:text-gray-200">
                <li>You must be in the same place</li>
                <li>Shared screen (less privacy)</li>
                <li>Best with a larger screen</li>
              </ul>
            </div>
          </div>

          <div className="text-center">
            <LocalizedLink
              to="/blog/modalidad/mesa"
              className="inline-block px-6 py-3 bg-white dark:bg-gray-900 border-2 border-purple-600 dark:border-purple-400 text-purple-600 dark:text-purple-200 rounded-lg font-semibold hover:bg-purple-50 dark:hover:bg-gray-800 transition-colors mr-3"
            >
              📖 Read more
            </LocalizedLink>
            <LocalizedLink
              to="/table"
              className="inline-block px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg font-semibold hover:from-purple-700 hover:to-pink-700 transition-colors"
            >
              🎲 Play Table Mode
            </LocalizedLink>
          </div>
        </div>

        {/* Comparison */}
        <div className="mt-12">
          <h2 className="text-2xl font-bold text-indigo-800 mb-6 text-center">
            📊 Mode Comparison
          </h2>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-gray-900 dark:text-gray-100">
              <thead>
                <tr className="bg-indigo-100 dark:bg-gray-900">
                  <th className="border border-indigo-300 dark:border-gray-800 px-4 py-3 text-left">Feature</th>
                  <th className="border border-indigo-300 dark:border-gray-800 px-4 py-3 text-center">Multiplayer</th>
                  <th className="border border-indigo-300 dark:border-gray-800 px-4 py-3 text-center">Table Mode</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="border border-gray-300 dark:border-gray-800 px-4 py-2">Devices needed</td>
                  <td className="border border-gray-300 dark:border-gray-800 px-4 py-2 text-center">One per player</td>
                  <td className="border border-gray-300 dark:border-gray-800 px-4 py-2 text-center">Only one</td>
                </tr>
                <tr className="bg-gray-50 dark:bg-gray-800/50">
                  <td className="border border-gray-300 dark:border-gray-800 px-4 py-2">Internet connection</td>
                  <td className="border border-gray-300 dark:border-gray-800 px-4 py-2 text-center">Everyone</td>
                  <td className="border border-gray-300 dark:border-gray-800 px-4 py-2 text-center">Host only</td>
                </tr>
                <tr>
                  <td className="border border-gray-300 dark:border-gray-800 px-4 py-2">Remote play</td>
                  <td className="border border-gray-300 dark:border-gray-800 px-4 py-2 text-center">✅ Yes</td>
                  <td className="border border-gray-300 dark:border-gray-800 px-4 py-2 text-center">❌ No</td>
                </tr>
                <tr className="bg-gray-50 dark:bg-gray-800/50">
                  <td className="border border-gray-300 dark:border-gray-800 px-4 py-2">In-person play</td>
                  <td className="border border-gray-300 dark:border-gray-800 px-4 py-2 text-center">✅ Yes</td>
                  <td className="border border-gray-300 dark:border-gray-800 px-4 py-2 text-center">✅ Yes</td>
                </tr>
                <tr>
                  <td className="border border-gray-300 dark:border-gray-800 px-4 py-2">Role privacy</td>
                  <td className="border border-gray-300 dark:border-gray-800 px-4 py-2 text-center">High (individual screen)</td>
                  <td className="border border-gray-300 dark:border-gray-800 px-4 py-2 text-center">High (turn-based individual view)</td>
                </tr>
                <tr className="bg-gray-50 dark:bg-gray-800/50">
                  <td className="border border-gray-300 dark:border-gray-800 px-4 py-2">Best for</td>
                  <td className="border border-gray-300 dark:border-gray-800 px-4 py-2 text-center">Remote play</td>
                  <td className="border border-gray-300 dark:border-gray-800 px-4 py-2 text-center">Parties and gatherings</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* Final CTA */}
        <div className="mt-12 text-center bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-gray-900 dark:to-gray-950 border-2 border-indigo-200 dark:border-gray-800 rounded-xl p-8">
          <h3 className="text-2xl font-bold text-indigo-900 dark:text-gray-100 mb-3">Ready to play?</h3>
          <p className="text-gray-700 dark:text-gray-200 mb-6">Choose your favorite mode and start playing</p>
          <div className="flex flex-wrap justify-center gap-3">
            <LocalizedLink
              to="/create"
              className="px-8 py-4 bg-indigo-600 text-white rounded-lg font-bold text-lg hover:bg-indigo-700 transition-colors shadow-lg"
            >
              🌐 Play Online
            </LocalizedLink>
            <LocalizedLink
              to="/table"
              className="px-8 py-4 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg font-bold text-lg hover:from-purple-700 hover:to-pink-700 transition-colors shadow-lg"
            >
              🎲 Play Table Mode
            </LocalizedLink>
          </div>
        </div>
      </ArticleContainer>
    </BlogLayout>
  );
};

const BlogModalidades: React.FC = () => {
  const { i18n } = useTranslation();
  if (i18n.language === 'en') return <BlogModalidadesEn />;
  if (i18n.language === 'pt') return <BlogModalidadesPt />;
  return <BlogModalidadesEs />;
};

export default BlogModalidades;
